import React from 'react';
import { Link } from 'react-router-dom';

export default function CookiesPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-emerald-50 to-orange-50">
      <header className="bg-black text-white">
        <nav className="max-w-7xl mx-auto px-4 py-3 flex items-center">
          <div className="flex items-center space-x-6">
            <Link to="/" className="flex items-center">
              <img
                src="https://rentreview.io/wp-content/uploads/2025/05/Rent-Review-Logo.png"
                alt="RentReview"
                className="h-9"
              />
            </Link>
            <Link to="/leave-review" className="text-sm">Leave a Review</Link>
          </div>
        </nav>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-sm p-12">
          <div className="space-y-8">
            <div className="border-b pb-8">
              <h1 className="text-4xl font-bold mb-4">Cookies Policy</h1>
              <div className="text-gray-600">
                <p>Effective Date: 25th November 2025</p>
                <p>Last Updated: 25th November 2025</p>
              </div>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 mb-6">
                This Cookies Policy explains what cookies are, how we use them on RentReview, and how you can manage your cookie preferences. By using our website, you consent to our use of cookies in accordance with this policy.
              </p>

              <h2 className="text-2xl font-bold mt-8 mb-4">1. What Are Cookies?</h2>
              <p className="text-gray-700 mb-4">
                Cookies are small text files that are placed on your device (computer, smartphone, or tablet) when you visit a website. They are widely used to make websites work more efficiently and provide information to website owners.
              </p>
              <p className="text-gray-700 mb-4">
                Cookies can be "session cookies" (which are deleted when you close your browser) or "persistent cookies" (which remain on your device for a set period or until you delete them).
              </p>

              <h2 className="text-2xl font-bold mt-8 mb-4">2. How We Use Cookies</h2>
              <p className="text-gray-700 mb-4">
                We use cookies for the following purposes:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Essential Cookies:</strong> Required for the website to function properly, including authentication and security features</li>
                <li><strong>Analytics Cookies:</strong> To understand how visitors use our website, which pages are most popular, and how users navigate the site</li>
                <li><strong>Functionality Cookies:</strong> To remember your preferences and settings, such as cookie consent choices</li>
              </ul>

              <h2 className="text-2xl font-bold mt-8 mb-4">3. Types of Cookies We Use</h2>

              <div className="mt-6">
                <h3 className="text-xl font-semibold mb-3">Essential Cookies</h3>
                <p className="text-gray-700 mb-4">
                  These cookies are strictly necessary for our website to function. They enable core functionality such as security and accessibility features. You cannot opt out of these cookies as the website will not function properly without them.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <p className="text-sm text-gray-700"><strong>Cookie:</strong> cookieConsent</p>
                  <p className="text-sm text-gray-700"><strong>Purpose:</strong> Stores your cookie consent preferences</p>
                  <p className="text-sm text-gray-700"><strong>Duration:</strong> 12 months</p>
                  <p className="text-sm text-gray-700"><strong>Type:</strong> First-party</p>
                </div>
              </div>

              <div className="mt-6">
                <h3 className="text-xl font-semibold mb-3">Analytics Cookies</h3>
                <p className="text-gray-700 mb-4">
                  We use Google Analytics to collect information about how visitors use our website. This helps us improve our website and provide a better user experience.
                </p>
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <p className="text-sm text-gray-700"><strong>Service:</strong> Google Analytics (GA4)</p>
                  <p className="text-sm text-gray-700"><strong>Cookies:</strong> _ga, _ga_*, _gid, _gat</p>
                  <p className="text-sm text-gray-700"><strong>Purpose:</strong> Track and analyse website usage, understand user behaviour, and generate statistical reports</p>
                  <p className="text-sm text-gray-700"><strong>Duration:</strong> Varies (from session to 2 years)</p>
                  <p className="text-sm text-gray-700"><strong>Type:</strong> Third-party</p>
                  <p className="text-sm text-gray-700"><strong>Provider:</strong> Google LLC</p>
                </div>
                <p className="text-gray-700 text-sm mb-4">
                  For more information about how Google uses data, please visit:{' '}
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                    Google Privacy Policy
                  </a>
                </p>
              </div>

              <h2 className="text-2xl font-bold mt-8 mb-4">4. Managing Your Cookie Preferences</h2>
              <p className="text-gray-700 mb-4">
                You can manage your cookie preferences in several ways:
              </p>

              <div className="mt-6">
                <h3 className="text-xl font-semibold mb-3">On Our Website</h3>
                <p className="text-gray-700 mb-4">
                  When you first visit our website, you will see a cookie consent banner. You can choose to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                  <li><strong>Accept All:</strong> Allow all cookies, including analytics</li>
                  <li><strong>Reject Non-Essential:</strong> Only allow essential cookies required for the website to function</li>
                  <li><strong>Learn More:</strong> Read this policy before making a decision</li>
                </ul>
                <p className="text-gray-700 mb-4">
                  You can change your preferences at any time by clearing your browser cookies and revisiting our website.
                </p>
              </div>

              <div className="mt-6">
                <h3 className="text-xl font-semibold mb-3">Through Your Browser</h3>
                <p className="text-gray-700 mb-4">
                  Most web browsers allow you to control cookies through their settings. You can set your browser to refuse cookies or alert you when cookies are being sent. However, please note that some features of our website may not function properly if you disable cookies.
                </p>
                <p className="text-gray-700 mb-4">
                  Here are links to cookie management guides for popular browsers:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                  <li>
                    <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                      Google Chrome
                    </a>
                  </li>
                  <li>
                    <a href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                      Mozilla Firefox
                    </a>
                  </li>
                  <li>
                    <a href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                      Safari (macOS)
                    </a>
                  </li>
                  <li>
                    <a href="https://support.apple.com/en-gb/HT201265" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                      Safari (iOS)
                    </a>
                  </li>
                  <li>
                    <a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                      Microsoft Edge
                    </a>
                  </li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-xl font-semibold mb-3">Opt Out of Google Analytics</h3>
                <p className="text-gray-700 mb-4">
                  You can also opt out of Google Analytics tracking by installing the Google Analytics Opt-out Browser Add-on, available at:{' '}
                  <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                    https://tools.google.com/dlpage/gaoptout
                  </a>
                </p>
              </div>

              <h2 className="text-2xl font-bold mt-8 mb-4">5. Third-Party Cookies</h2>
              <p className="text-gray-700 mb-4">
                Some cookies on our website are set by third-party services. We do not control these cookies or how the third parties use them. We recommend you check the third-party websites for more information about their cookies and how to manage them.
              </p>
              <p className="text-gray-700 mb-4">
                Currently, we use:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Google Analytics:</strong> For website analytics and performance measurement</li>
              </ul>

              <h2 className="text-2xl font-bold mt-8 mb-4">6. Updates to This Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this Cookies Policy from time to time to reflect changes in technology, legislation, or our business practices. When we make changes, we will update the "Last Updated" date at the top of this policy.
              </p>
              <p className="text-gray-700 mb-4">
                We encourage you to review this policy periodically to stay informed about how we use cookies.
              </p>

              <h2 className="text-2xl font-bold mt-8 mb-4">7. Contact Us</h2>
              <p className="text-gray-700 mb-2">
                If you have any questions about our use of cookies, please contact us:
              </p>
              <p className="text-gray-700 mb-2">
                <strong>Email:</strong> <a href="mailto:info@rentreview.io" className="text-blue-600 hover:text-blue-800">info@rentreview.io</a>
              </p>
              <p className="text-gray-700 mb-2">
                <strong>Business Name:</strong> Profect Media Ltd Trading as Rent.Reviews
              </p>
              <p className="text-gray-700">
                <strong>Address:</strong> 128 City Road, London, EC1V 2NJ
              </p>

              <h2 className="text-2xl font-bold mt-8 mb-4">8. Related Policies</h2>
              <p className="text-gray-700">
                For more information about how we handle your personal data, please see our{' '}
                <Link to="/privacy-policy" className="text-blue-600 hover:text-blue-800 underline">
                  Privacy Policy
                </Link>
                {' '}and{' '}
                <Link to="/terms-conditions" className="text-blue-600 hover:text-blue-800 underline">
                  Terms & Conditions
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
